import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  // Woof&Meow: swipe between images in the zoomed view on phones.
  // drag-zoom-wrapper cancels native touch scrolling (it owns pinch and pan),
  // so a one-finger sideways swipe at 1x zoom moves the gallery here instead.
  /** @type {{ x: number, y: number, time: number, lastX: number, scrollLeft: number, index: number, axis: 'x' | 'y' | null } | null} */
  #swipe = null;
  #swipeController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);

    const options = { passive: true, signal: this.#swipeController.signal };
    this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, options);
    this.refs.dialog.addEventListener('touchmove', this.#handleSwipeMove, options);
    this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, options);
    this.refs.dialog.addEventListener('touchcancel', this.#handleSwipeEnd, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.#swipeController.abort();
  }

  get #gallery() {
    return /** @type {HTMLElement | null} */ (this.refs.dialog.querySelector('.dialog-zoomed-gallery'));
  }

  /**
   * @param {TouchEvent} event
   */
  #handleSwipeStart = (event) => {
    this.#swipe = null;

    const gallery = this.#gallery;
    const touch = event.touches[0];
    if (!gallery || !touch || event.touches.length !== 1 || !isMobileBreakpoint()) return;
    if (!(event.target instanceof Element) || !gallery.contains(event.target)) return;
    if (event.target.closest('.product-media-container--model')) return;

    // Leave pan to drag-zoom-wrapper once the image is zoomed in
    const wrapper = /** @type {HTMLElement | null} */ (event.target.closest('drag-zoom-wrapper'));
    const scale = parseFloat(wrapper?.style.getPropertyValue('--drag-zoom-scale') || '1');
    if (scale > 1.01) return;

    const width = gallery.clientWidth || 1;
    this.#swipe = {
      x: touch.clientX,
      y: touch.clientY,
      time: performance.now(),
      lastX: touch.clientX,
      scrollLeft: gallery.scrollLeft,
      index: Math.round(gallery.scrollLeft / width),
      axis: null,
    };
  };

  /**
   * @param {TouchEvent} event
   */
  #handleSwipeMove = (event) => {
    const swipe = this.#swipe;
    const gallery = this.#gallery;
    if (!swipe || !gallery) return;

    // A second finger means pinch: hand the gesture back
    if (event.touches.length !== 1) {
      this.#finishSwipe(swipe.index);
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    const dx = touch.clientX - swipe.x;
    const dy = touch.clientY - swipe.y;
    swipe.lastX = touch.clientX;

    if (!swipe.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return;
      swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

      if (swipe.axis === 'x') {
        gallery.style.scrollSnapType = 'none';
        gallery.style.scrollBehavior = 'auto';
      }
    }

    if (swipe.axis === 'x') {
      gallery.scrollLeft = swipe.scrollLeft - dx;
    }
  };

  #handleSwipeEnd = () => {
    const swipe = this.#swipe;
    const gallery = this.#gallery;
    if (!swipe || !gallery) return;

    if (swipe.axis !== 'x') {
      this.#swipe = null;
      return;
    }

    const dx = swipe.lastX - swipe.x;
    const elapsed = performance.now() - swipe.time;
    const width = gallery.clientWidth || 1;
    const isFlick = Math.abs(dx) > 40 && elapsed < 300;

    let target = swipe.index;
    if (Math.abs(dx) > width * 0.2 || isFlick) {
      target = swipe.index + (dx < 0 ? 1 : -1);
    }

    this.#finishSwipe(target);
  };

  /**
   * Scrolls to the chosen image, then puts scroll snapping back.
   * @param {number} index
   */
  #finishSwipe(index) {
    const gallery = this.#gallery;
    this.#swipe = null;
    if (!gallery) return;

    const { media, thumbnails } = this.refs;
    const target = Math.min(Math.max(index, 0), media.length - 1);
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    const restoreSnap = () => {
      gallery.style.removeProperty('scroll-snap-type');
      gallery.style.removeProperty('scroll-behavior');
    };

    // scrollIntoView does not move the gallery from a mid-swipe position, so scroll it directly
    gallery.scrollTo({ left: target * gallery.clientWidth, behavior });

    if ('onscrollend' in window) {
      gallery.addEventListener('scrollend', restoreSnap, { once: true });
    }
    setTimeout(restoreSnap, 800);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === target}`);
    });

    const targetThumbnail = thumbnails.children[target];
    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, { ancestor: thumbnails, behavior, block: 'center', inline: 'center' });
    }

    const targetImage = media[target];
    if (targetImage) this.loadHighResolutionImage(targetImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(target));
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
